import React from 'react';
import {
  Zap,
  Rocket,
  TreePine,
  Leaf,
  Waves,
  Fish,
  Heart,
  Flower,
  Sun,
  CloudSun,
  Moon,
  Stars,
  Grape,
  Coffee,
  Snowflake,
  Sparkles,
  Flame,
  Mountain,
  Sparkle,
  Circle,
  Leaf as MapleLeaf,
  Nut,
  Wind,
  TreePalm,
  Apple,
  type LucideIcon
} from 'lucide-react';
import { useTheme, type Theme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

// Define icon mappings for each theme
const THEME_ICONS: Record<Theme, { primary: LucideIcon; secondary: LucideIcon }> = {
  light: { primary: Zap, secondary: Rocket },
  dark: { primary: Moon, secondary: Stars },
  forest: { primary: TreePine, secondary: Leaf },
  ocean: { primary: Waves, secondary: Fish },
  cherry: { primary: Heart, secondary: Flower },
  sunset: { primary: Sun, secondary: CloudSun },
  midnight: { primary: Moon, secondary: Stars },
  grape: { primary: Grape, secondary: Coffee },
  mint: { primary: Leaf, secondary: Snowflake },
  volcano: { primary: Flame, secondary: Mountain },
  galaxy: { primary: Sparkle, secondary: Circle },
  autumn: { primary: MapleLeaf, secondary: Nut },
  arctic: { primary: Snowflake, secondary: Wind },
  tropical: { primary: TreePalm, secondary: Apple },
};

interface ThemeAwareFloatingIconsProps {
  className?: string;
}

export const ThemeAwareFloatingIcons: React.FC<ThemeAwareFloatingIconsProps> = ({ 
  className 
}) => {
  const { theme } = useTheme();
  const icons = THEME_ICONS[theme];
  
  const PrimaryIcon = icons.primary;
  const SecondaryIcon = icons.secondary;

  return (
    <div className={cn('relative', className)}>
      {/* Primary Icon - Top Right */}
      <div className='absolute -top-85 -right-4 w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-primary/20 transition-all duration-500 hover:scale-110 hover:bg-primary/20'>
        <PrimaryIcon className='w-8 h-8 text-primary transition-all duration-500' />
      </div>
      
      {/* Secondary Icon - Bottom Left */}
      <div className='absolute -bottom-4 -left-4 w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-primary/20 transition-all duration-500 hover:scale-110 hover:bg-primary/20'>
        <SecondaryIcon className='w-6 h-6 text-primary transition-all duration-500' />
      </div>
    </div>
  );
};

// Alternative component for more elaborate floating icon arrangements
interface ThemeAwareFloatingIconsExtendedProps {
  className?: string;
  variant?: 'minimal' | 'extended' | 'constellation';
}

export const ThemeAwareFloatingIconsExtended: React.FC<ThemeAwareFloatingIconsExtendedProps> = ({ 
  className,
  variant = 'minimal'
}) => {
  const { theme } = useTheme();
  const icons = THEME_ICONS[theme];
  
  const PrimaryIcon = icons.primary;
  const SecondaryIcon = icons.secondary;

  if (variant === 'minimal') {
    return <ThemeAwareFloatingIcons className={className} />;
  }

  if (variant === 'extended') {
    return (
      <div className={cn('relative', className)}>
        {/* Primary Icon - Top Right */}
        <div className='absolute -top-4 -right-4 w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-primary/20 transition-all duration-500 hover:scale-110 hover:bg-primary/20 animate-pulse'>
          <PrimaryIcon className='w-8 h-8 text-primary transition-all duration-500' />
        </div>
        
        {/* Secondary Icon - Bottom Left */}
        <div className='absolute -bottom-4 -left-4 w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-primary/20 transition-all duration-500 hover:scale-110 hover:bg-primary/20 animate-pulse [animation-delay:1s]'>
          <SecondaryIcon className='w-6 h-6 text-primary transition-all duration-500' />
        </div>
        
        {/* Additional accent icons for extended variant */}
        <div className='absolute -top-2 -left-2 w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-accent/30 transition-all duration-500 hover:scale-110 animate-pulse [animation-delay:2s]'>
          <Sparkles className='w-4 h-4 text-accent transition-all duration-500' />
        </div>
        
        <div className='absolute -bottom-2 -right-2 w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-accent/30 transition-all duration-500 hover:scale-110 animate-pulse [animation-delay:3s]'>
          <Sparkles className='w-4 h-4 text-accent transition-all duration-500' />
        </div>
      </div>
    );
  }

  if (variant === 'constellation') {
    return (
      <div className={cn('relative', className)}>
        {/* Main constellation of theme-aware icons */}
        <div className='absolute -top-6 -right-6 w-20 h-20 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-primary/20 transition-all duration-500 hover:scale-110 hover:bg-primary/20 animate-pulse'>
          <PrimaryIcon className='w-10 h-10 text-primary transition-all duration-500' />
        </div>
        
        <div className='absolute -bottom-6 -left-6 w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-primary/20 transition-all duration-500 hover:scale-110 hover:bg-primary/20 animate-pulse [animation-delay:1s]'>
          <SecondaryIcon className='w-8 h-8 text-primary transition-all duration-500' />
        </div>
        
        {/* Smaller orbiting icons */}
        <div className='absolute top-0 left-0 w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-accent/30 transition-all duration-500 hover:scale-110 animate-pulse [animation-delay:2s]'>
          <Sparkles className='w-5 h-5 text-accent transition-all duration-500' />
        </div>
        
        <div className='absolute top-1/2 -right-2 w-8 h-8 bg-secondary/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-secondary/30 transition-all duration-500 hover:scale-110 animate-pulse [animation-delay:3s]'>
          <Sparkles className='w-4 h-4 text-secondary-foreground transition-all duration-500' />
        </div>
        
        <div className='absolute bottom-0 left-1/2 w-8 h-8 bg-muted/40 rounded-full flex items-center justify-center backdrop-blur-sm border border-muted-foreground/20 transition-all duration-500 hover:scale-110 animate-pulse [animation-delay:4s]'>
          <Sparkles className='w-4 h-4 text-muted-foreground transition-all duration-500' />
        </div>
      </div>
    );
  }

  return <ThemeAwareFloatingIcons className={className} />;
};

export default ThemeAwareFloatingIcons;
